/**
 * CodeMirror state effects and fields for tokens and suggestions
 */

import {StateEffect, StateField} from "@codemirror/state";
import {Token, Suggestion, clampToken} from "../models/editor.types";

// ===== Token State =====
export const setTokensEffect = StateEffect.define<Token[]>();

export const tokensField = StateField.define<Token[]>({
  create() {
    return [];
  },
  update(tokens, tr) {
    const docLen = tr.state.doc.length;
    let mapped = tokens
      .map((t) => ({
        ...t,
        // Draft tokens: from stays left (-1), to goes right (1) to expand
        // Other tokens: both map with 1 to stay anchored
        from: tr.changes.mapPos(t.from, t.type === "ingredient_draft" ? -1 : 1),
        to: tr.changes.mapPos(t.to, t.type === "ingredient_draft" ? 1 : -1),
      }))
      .map((t) => clampToken(t, docLen))
      // Allow empty draft tokens (to === from), but remove other empty tokens
      .filter((t) => t.to > t.from || t.type === "ingredient_draft");

    for (const e of tr.effects) {
      if (e.is(setTokensEffect)) {
        return e.value.map((t) => clampToken(t, docLen))
          // Allow empty draft tokens when setting via effect
          .filter((t) => t.to > t.from || t.type === "ingredient_draft");
      }
    }

    return mapped;
  },
});

// ===== Suggestion State =====
export const setSuggestionsEffect = StateEffect.define<Suggestion[]>();

export const suggestionsField = StateField.define<Suggestion[]>({
  create() {
    return [];
  },
  update(suggestions, tr) {
    // Map existing suggestions through document changes
    const mapped = suggestions
      .map((s) => ({
        ...s,
        from: tr.changes.mapPos(s.from, 1),
        to: tr.changes.mapPos(s.to, -1),
      }))
      .filter((s) => s.to > s.from);

    // Replace suggestions via effect
    for (const e of tr.effects) {
      if (e.is(setSuggestionsEffect)) {
        const docLen = tr.state.doc.length;
        return e.value.map((s) => ({
          ...s,
          from: Math.max(0, Math.min(docLen, s.from)),
          to: Math.max(0, Math.min(docLen, s.to)),
        })).filter((s) => s.to > s.from);
      }
    }

    return mapped;
  },
});
